<template>
  <div>
    <div
      v-if="!shouldShowCardForm && !isPaypal"
      class="flex flex-col items-center"
    >
      <Card :class="$style.card" variant="secondary">
        <div class="mb-2">
          <Typography tag="div">{{ $i18n('Numéro de carte') }}</Typography>

          <Typography :variant="TYPOGRAPHY_TYPES.bodyBold" tag="div">
            {{ cardHolderName }}
          </Typography>
        </div>

        <div v-if="cardNumber" class="mb-2">
          <Typography tag="div">{{ $i18n('Numéro de carte') }}</Typography>

          <Typography tag="div" :variant="TYPOGRAPHY_TYPES.bodyBold">
            {{ cardNumber }}
          </Typography>
        </div>

        <div v-if="expirationDate" class="mb-2">
          <Typography tag="div">{{ $i18n("Date d'expiration") }}</Typography>

          <Typography tag="div" :variant="TYPOGRAPHY_TYPES.bodyBold">
            {{ expirationDate }}
          </Typography>
        </div>
      </Card>

      <Button
        :class="$style.modifyCardButton"
        appearance="link"
        @click="toggleCardForm"
      >
        {{ $i18n('Modifier ma carte') }}
      </Button>
    </div>

    <div v-if="shouldShowCardForm || isPaypal" :class="$style.formWrapper">
      <Typography
        v-if="isPaypal"
        :variant="TYPOGRAPHY_TYPES.bodySmallRegular"
        :class="$style.noCardWording"
      >
        {{
          $i18n(
            "Vous n'avez actuellement aucune carte enregistrée. Veuillez en ajouter une ci-dessous.",
          )
        }}
      </Typography>

      <CardForm
        class="w-full"
        :error="errorMessage"
        :is-loading="isLoading"
        @submit="updateCardInformation"
      />

      <div :class="$style.buttons">
        <Button v-if="!isPaypal" appearance="link" @click="toggleCardForm">
          {{ $i18n('Annuler') }}
        </Button>
      </div>
    </div>
  </div>
</template>
<script>
import { SUCCESS } from '@front/common/constants'
import { isObjectEmpty } from '@front/common/utils'
import { Button, Card, Typography, TYPOGRAPHY_TYPES } from '@front/ui'
import useAlert from '@front/ui/Alert/useAlert'
import { computed, defineComponent, watch, ref } from '@vue/composition-api'

import useCustomer from '@member-area/core/resources/Customer/Customer.hook'
import usePaymentMethod from '@member-area/core/resources/PaymentMethod/PaymentMethod.hook'

import CardForm from './CardForm.vue'

export default defineComponent({
  components: { CardForm, Typography, Button, Card },
  setup() {
    const { triggerAlert } = useAlert()
    const { customer, fetchCustomer } = useCustomer()
    const { addPaymentMethod, fetchPaymentMethod, paymentMethod } =
      usePaymentMethod()

    fetchPaymentMethod()

    const shouldShowCardForm = ref(false)
    const isPaypal = ref(false)
    const isLoading = ref(false)
    const errorMessage = ref()

    const cardHolderName = computed(() => {
      const firstName = customer.value?.firstName || ''
      const lastName = customer.value?.lastName || ''

      return firstName && lastName ? `${lastName} ${firstName}` : ''
    })

    const expirationDate = computed(() => {
      return paymentMethod.value.expiration || ''
    })

    const cardNumber = computed(() => {
      const { lastNumber } = paymentMethod.value

      return lastNumber ? `XXXX XXXX XXXX ${lastNumber}` : ''
    })

    async function toggleCardForm() {
      shouldShowCardForm.value = !shouldShowCardForm.value
    }

    async function updateCardInformation(card, stripe) {
      isLoading.value = true

      try {
        await addPaymentMethod({ cardNumber: card, stripe })
        await fetchCustomer()

        isLoading.value = false

        toggleCardForm()
        triggerAlert({
          status: SUCCESS,
          message: 'Votre carte est en cours de vérification.',
        })
      } catch ({ message }) {
        errorMessage.value = message

        isLoading.value = false
      }
    }

    watch(
      paymentMethod,
      (value) => {
        isPaypal.value =
          Boolean(isObjectEmpty(value)) || value.provider === 'paypal'
      },
      { immediate: true },
    )

    return {
      updateCardInformation,
      cardHolderName,
      expirationDate,
      cardNumber,
      toggleCardForm,
      shouldShowCardForm,
      TYPOGRAPHY_TYPES,
      isPaypal,
      errorMessage,
      isLoading,
    }
  },
})
</script>

<style lang="scss" module>
.card {
  width: 100%;
}

.modifyCardButton {
  margin-top: $ds-spacing-xs;
}

.formWrapper {
  display: flex;
  flex-direction: column;
}

.buttons {
  display: inline-flex;
  margin: 0px auto;
  gap: $ds-spacing-md;
  margin-top: $ds-spacing-md;
}

.noCardWording {
  margin-bottom: $ds-spacing-xs;
}
</style>
